"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { type DateRange } from "react-day-picker"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export interface DateRangePickerProps {
  /** The currently selected date range */
  value: DateRange | undefined
  /** Callback when date range selection changes */
  onChange: (date: DateRange | undefined) => void
  /** Placeholder text when no date is selected */
  placeholder?: string
  /** Optional class name for the container */
  className?: string
  /** Optional HTML attributes for the container div */
  containerProps?: Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Select date range",
  className,
  containerProps,
}: DateRangePickerProps) {
  return (
    <div 
      {...containerProps}
      className={cn("grid gap-2", className)}
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !value?.from && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value?.from ? (
              value.to ? (
                <>
                  {format(value.from, "LLL dd, y")} -{" "}
                  {format(value.to, "LLL dd, y")}
                </>
              ) : (
                format(value.from, "LLL dd, y")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={onChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
